import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { ToastAction } from '@/components/ui/toast';
import { toast } from '@/hooks/useToast';

interface CampaignToastRef {
  name?: string | null;
  slug?: string | null;
}

interface CampaignErrorToastArgs {
  /** Generic verb line, e.g. "Couldn't deploy changes". */
  title: string;
  /** The specific reason (API message); joined after the campaign name. */
  message?: string | null;
  campaign?: CampaignToastRef | null;
}

/**
 * Destructive toast that names the affected campaign and carries an Open
 * action jumping straight to it (#5782). Async failures (launch, deploy,
 * sync) often fire after the user navigated away, so the toast must say
 * WHICH campaign failed and take them back in one click.
 */
export function useCampaignErrorToast() {
  const navigate = useNavigate();
  return useCallback(
    ({ title, message, campaign }: CampaignErrorToastArgs) => {
      const name = campaign?.name?.trim();
      const slug = campaign?.slug;
      const description =
        [name ? `Campaign "${name}"` : null, message?.trim() || null].filter(Boolean).join(': ') || undefined;
      toast({
        title,
        description,
        variant: 'destructive',
        action: slug ? (
          <ToastAction
            altText={name ? `Open ${name}` : 'Open campaign'}
            onClick={() => void navigate({ to: '/campaigns/$campaignslug', params: { campaignslug: slug } })}
          >
            Open
          </ToastAction>
        ) : undefined,
      });
    },
    [navigate],
  );
}
